import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { AppBar } from "@/elements/components/ui/app-bar";
import { useLocalSearchParams } from "expo-router";
import { ScrollView, View, Text } from "react-native";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { Textarea } from "@/elements/components/ui/textarea";
import { Button } from "@/elements/components/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import goBack from "@/elements/components/utils/go-back";
import NoShowReportedModal from "../modals/no-show-reported-modal";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "../utils/convex-api";

const NoShowFormSchema = z.object({
  details: z.string().optional(),
});

function TutorNoShowScreen() {
  const { lessonId } = useLocalSearchParams();
  const { control, handleSubmit, formState } = useForm({
    defaultValues: {
      details: "",
    },
    resolver: zodResolver(NoShowFormSchema),
    mode: "onChange",
  });
  const [isSuccess, setIsSuccess] = useState(false);

  const { mutateAsync: reportNoShow, isPending } = useMutation({
    mutationFn: useConvexMutation(api.student.noShow.mutations.reportTutorNoShow),
    onSuccess: () => {
      setIsSuccess(true);
    },
  });

  const onClose = () => goBack();
  const onModalClose = () => goBack(true);

  const onReportNoShow = handleSubmit(async ({ details }) => {
    if (!lessonId || typeof lessonId !== 'string') return;
    
    await reportNoShow({
      sessionId: lessonId as any,
      reason: details,
    });
  });

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon onPress={onClose} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5 gap-6">
            <View className="py-2">
              <View className="gap-8">
                <View className="gap-4">
                  <Text className="font-zilla-slab-medium font-medium text-[28px] leading-[1.2] text-black">
                    Report a no show
                  </Text>
                  <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-teachLoop-textPrimary">
                    We are sorry to hear that your tutor has not turned up to the session. Please report it so we can support you & resolve this quickly.
                  </Text>
                </View>

                <Controller
                  control={control}
                  name="details"
                  render={({ field, fieldState: { error } }) => (
                    <Textarea
                      placeholder="Add any details you feel necessary."
                      className="h-[167]"
                      size="sm"
                      multiline
                      numberOfLines={4}
                      textAlignVertical="top"
                      {...field}
                      error={!!error?.message}
                    />
                  )}
                />

                <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-teachLoop-textPrimary">
                  An email will be sent to the tutor and admin. You cannot undo this action.
                </Text>

                <Button
                  fullWidth
                  size="md"
                  intent="danger"
                  className="bg-red-600"
                  onPress={onReportNoShow}
                  disabled={isPending}
                >
                  <Button.Text size="md">Report no show</Button.Text>
                </Button>
              </View>
              <View className="h-3" />
            </View>
          </View>
          <View className="h-8" />
        </ResponsiveContent>
      </ScrollView>

      <NoShowReportedModal
        isVisible={isSuccess}
        onClose={() => {
          setIsSuccess(false);
          onModalClose();
        }}
        onDismiss={() => {
          setIsSuccess(false);
          onClose();
        }}
      />
    </View>
  );
}

export default TutorNoShowScreen;
